import { Factory } from "@/data/dao/Factory";
import { FeedbackDao } from "@/data/dao/service/FeedbackDao";
import { RatingValue } from "@/data/model/Enums";
import type { IAppRating } from "@/data/model/Rating";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { UiNotification } from "@/portal/ui/Notification";

export class FeedbackTableController {
    private $element: JQuery;
    private dao: FeedbackDao;

    private dataTable: DataTable<IAppRating>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<FeedbackDao>(FeedbackDao);

        const tableConfig: IDataTableConfig<IAppRating> = {
            columnMapping: [
                {
                    name: i18n("feedback.fields.rating"),
                    property: "rating",
                    styles: { width: "20%" },
                    formatter: (m, p) => {
                        let classN = "success";
                        switch (p) {
                            case RatingValue.VeryBad:
                                classN = "danger";
                                break;
                            case RatingValue.Bad:
                                classN = "danger";
                                break;
                            case RatingValue.Ok:
                                classN = "warning";
                                break;
                            case RatingValue.Good:
                                classN = "success";
                                break;
                            case RatingValue.VeryGood:
                                classN = "success";
                                break;
                        }

                        return $(
                            '<div class="kt-badge kt-badge--' +
                                classN +
                                ' kt-badge--inline">' +
                                i18n("enums.RatingValue." + RatingValue[p]) +
                                "</div>"
                        );
                    },
                },
                {
                    name: i18n("feedback.fields.created"),
                    property: "created",
                    styles: { width: "20%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("general.entities.user"),
                    property: "userId",
                    styles: { width: "28%" },
                    formatter: (m, p) => {
                        if (p === undefined)
                            return m.userName === undefined
                                ? i18n("general.deletedUser")
                                : Format.htmlEscape(m.userName);

                        return $('<a href="/user/' + p + '">' + Format.htmlEscape(m.userName) + "</a>").click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: i18n("feedback.fields.comment"),
                    property: "comment",
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: (m, p) => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-trash"></i></a></span>'
                        )
                            .data("id", p)
                            .click((e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                UiNotification.confirm(
                                    i18n("feedback.reallyDelete"),
                                    undefined,
                                    () => {
                                        Factory.getInstance<FeedbackDao>(FeedbackDao)
                                            .removeFeedback($(e.currentTarget).data("id"))
                                            .then(() => {
                                                this.dataTable.loadData(true);
                                            })
                                            .catch((ex) => {
                                                logError(ex);
                                                UiNotification.showException(i18n("feedback.failedDelete"));
                                            });
                                    },
                                    i18n("general.remove")
                                );
                            });
                    },
                },
            ],
            defaultSortAsc: false,
            defaultSortProperty: "created",
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromEnum("rating", RatingValue, "enums.RatingValue", i18n("feedback.allRatings"));
        this.dataTable = new DataTable<IAppRating>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.listenTo(this.search);
    }
}
